import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Select, Text } from '@vymo/ui/atoms';
import { RangePicker } from '@vymo/ui/blocks';
import Table from '@vymo/ui/blocks/table';
import useContainerSize from 'src/hooks/useContainerSize';
import { Keys, locale } from 'src/i18n';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { getLogEvents } from '../selectors';
import { fetchLogEvents } from '../thunk';
import { timeRangeOptions } from './constants';
import { buildQuery, getSelectOptions, getStartEndTime } from './queries';
import { Activity, QueryParams } from './types';
import styles from './index.module.scss';

function renderCellValue(val: any) {
  if (val === null || val === undefined) return '';
  if (typeof val === 'object') {
    try {
      return <pre>{JSON.stringify(val, null, 2)}</pre>; // nicely formatted
    } catch {
      return String(val);
    }
  }
  return String(val);
}

const columns = [
  {
    title: 'ID',
    field: '_id',
    type: 'string',
    cell: (value: any) => renderCellValue(value),
  },
  {
    title: 'User ID',
    field: 'userId',
    type: 'string',
    cell: (value: any) => renderCellValue(value),
  },
  {
    title: 'Event',
    field: 'event',
    type: 'string',
    cell: (value: any) => renderCellValue(value),
  },
  {
    title: 'App',
    field: 'app',
    type: 'string',
    cell: (value: any) => renderCellValue(value),
  },
  {
    title: 'Metadata',
    field: 'metadata',
    type: 'object',
    cell: (value: any) => renderCellValue(value),
  },
  {
    title: 'Timestamp',
    field: 'timestamp',
    type: 'string',
    cell: (value: any) => renderCellValue(value),
  },
];

// eslint-disable-next-line react/function-component-definition
const TrackingPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const logEvents = useAppSelector(getLogEvents);

  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState<QueryParams>({
    rangeType: '5m',
    startTime: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
    endTime: new Date().toISOString(),
  });

  const [activities, setActivities] = useState<Activity[]>([]);

  const [filterOptions, setFilterOptions] = useState<{
    apps: string[];
    events: string[];
    envs: string[];
  }>({ apps: [], events: [], envs: [] });

  const [containerRef, { height }] = useContainerSize();

  useEffect(() => {
    setError(null);
    dispatch(fetchLogEvents(buildQuery(query)));
  }, [dispatch, query]);

  useEffect(() => {
    setActivities(logEvents.activities || []);
    setFilterOptions(logEvents.filters ?? { apps: [], events: [], envs: [] });
  }, [logEvents]);

  const tableData = useMemo(() => activities, [activities]);

  const onChangeTimeRange = useCallback(
    (option) => {
      const value = Array.isArray(option) ? option[0]?.value : option?.value;
      const { startTime, endTime } = getStartEndTime(value, query);
      setQuery((q) => ({
        ...q,
        rangeType: value,
        startTime,
        endTime,
      }));
    },
    [query],
  );

  const onChangeRangePicker = (value) => {
    const [startDate, endDate] = value;
    if (startDate && endDate) {
      // Only allow custom range up to 90 days before now
      const now = new Date();
      const maxStart = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      const safeStart =
        startDate && new Date(startDate) < maxStart ? maxStart : startDate;
      setQuery((q) => ({
        ...q,
        startTime: safeStart ? new Date(safeStart).toISOString() : undefined,
        endTime: endDate ? new Date(endDate).toISOString() : undefined,
      }));
    }
  };

  return (
    <div className={styles.trackingPage}>
      <Text type="h3">{locale(Keys.TRACK_EVENTS)}</Text>
      <div className={styles.trackingPage__header}>
        <Select
          options={getSelectOptions(filterOptions.apps)}
          value={query.app}
          search
          placeholder={locale(Keys.SELECT_APP)}
          onChange={(option) => {
            const value = option[0]?.value;
            setQuery((q) => ({ ...q, app: value ?? '' }));
          }}
        />

        <Select
          options={getSelectOptions(filterOptions.events)}
          value={query.eventId}
          search
          placeholder={locale(Keys.SELECT_EVENT)}
          onChange={(option) => {
            const value = option[0]?.value;
            setQuery((q) => ({ ...q, eventId: value ?? '' }));
          }}
        />

        <Select
          options={getSelectOptions(filterOptions.envs)}
          value={query.env}
          search
          placeholder={locale(Keys.SELECT_ENV)}
          onChange={(option) => {
            const value = option[0]?.value;
            setQuery((q) => ({ ...q, env: value ?? '' }));
          }}
        />

        <Select
          options={timeRangeOptions}
          search
          value={query.rangeType}
          placeholder={locale(Keys.SELECT_TIME_RANGE)}
          onChange={onChangeTimeRange}
        />
        {query.rangeType === 'custom' && (
          // @ts-ignore
          <RangePicker onChange={onChangeRangePicker} />
        )}

        <Button
          onClick={() =>
            setQuery({
              rangeType: '5m',
              startTime: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
              endTime: new Date().toISOString(),
            })
          }
        >
          Reset
        </Button>
      </div>
      {error && (
        <Text type="h4" variant="error">
          {error}
        </Text>
      )}
      <div
        ref={containerRef}
        style={{ height }}
        className={styles.trackingPage__section}
      >
        <Table
          columnConfigs={columns}
          tableData={tableData}
          tableConfig={{
            name: 'tracking-table',
            pagination: { visible: true },
          }}
        />
      </div>
    </div>
  );
};

export default TrackingPage;
